import { useEffect, useState } from 'react';
import { api, type PlanInfo } from '../api';

function formatPrice(rub: string): string {
  const n = Number(rub);
  return Number.isFinite(n) ? `${n.toLocaleString('ru-RU')} ₽` : `${rub} ₽`;
}

function formatDuration(days: number): string {
  if (days % 30 === 0) {
    const months = days / 30;
    return months === 1 ? '1 мес.' : `${months} мес.`;
  }
  if (days % 365 === 0) {
    const years = days / 365;
    return years === 1 ? '1 год' : `${years} г.`;
  }
  return `${days} дн.`;
}

function monthlyPrice(plan: PlanInfo): number | null {
  const n = Number(plan.priceRub);
  if (!Number.isFinite(n) || plan.durationDays < 28) return null;
  const months = plan.durationDays / 30;
  if (months < 1) return null;
  return n / months;
}

export function PlansPage() {
  const [plans, setPlans] = useState<PlanInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [buying, setBuying] = useState<string | null>(null);

  useEffect(() => {
    api.plans().then((d) => setPlans(d.plans)).catch((e) => setError(e instanceof Error ? e.message : 'Ошибка'));
  }, []);

  async function handleBuy(code: string) {
    setBuying(code);
    setError(null);
    try {
      const res = await api.checkout(code);
      window.location.href = res.checkoutUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось создать платёж');
      setBuying(null);
    }
  }

  if (error && !plans) return <div className="container"><div className="error">{error}</div></div>;
  if (!plans) return <div className="container">Загрузка…</div>;

  // Best value = lowest price per month among multi-month plans.
  const monthly = plans.map((p) => ({ code: p.code, m: monthlyPrice(p) }));
  const cheapest = monthly
    .filter((x) => x.m !== null)
    .sort((a, b) => (a.m as number) - (b.m as number))[0];
  const bestCode = cheapest?.code ?? null;

  const sortedPlans = [...plans].sort((a, b) => Number(b.priceRub) - Number(a.priceRub));

  return (
    <div className="container">
      <h1>Тарифы</h1>
      {error ? <div className="error">{error}</div> : null}
      {plans.length === 0 ? (
        <div className="card"><p className="muted">Тарифы временно недоступны.</p></div>
      ) : (
        <div className="lk-plans">
          {sortedPlans.map((p) => {
            const m = monthlyPrice(p);
            const isBest = p.code === bestCode && plans.length > 1;
            return (
              <div key={p.code} className={`lk-plan${isBest ? ' lk-plan--best' : ''}`}>
                {isBest && <span className="lk-plan-badge">Выгодно</span>}
                <div className="lk-plan-title">{p.title}</div>
                <div className="lk-plan-price">
                  {formatPrice(p.priceRub)} <span>/ {formatDuration(p.durationDays)}</span>
                </div>
                {m !== null && (
                  <div className="lk-plan-permonth">≈ {formatPrice(Math.round(m).toString())} в месяц</div>
                )}
                <div className="lk-plan-tags">
                  {p.isRecurringEnabled && <span className="lk-plan-tag">Автопродление</span>}
                  {p.oneTimePaymentAllowed && <span className="lk-plan-tag">Можно однократно</span>}
                </div>
                <div className="lk-plan-spacer" />
                <button
                  type="button"
                  className="btn-block"
                  disabled={buying === p.code}
                  onClick={() => handleBuy(p.code)}
                >
                  {buying === p.code ? 'Переходим к оплате…' : 'Оплатить'}
                </button>
              </div>
            );
          })}
        </div>
      )}
      <p className="muted" style={{ marginTop: 8 }}>
        Оплата проходит через защищённую страницу Robokassa. Вопросы?{' '}
        <a href="https://t.me/orbanvpn_support" target="_blank" rel="noopener">Поддержка</a>.
      </p>
    </div>
  );
}
